import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { TrackPricing, AddOnService } from './useTrackData';

export const useBookingCalculation = (
  selectedPricing: TrackPricing | null,
  selectedAddOns: string[],
  addOns: AddOnService[],
  lightingFee: number,
  isMultiDateBooking: boolean,
  selectedDates: string[],
  userProfile: { role: string } | null
) => {
  const [paymentOption, setPaymentOption] = useState<'full' | 'deposit'>('full');

  const calculateTotal = () => {
    if (!selectedPricing) return 0;
    
    const addOnTotal = selectedAddOns.reduce((total, addOnId) => {
      const addOn = addOns.find(a => a.id === addOnId);
      return total + (addOn?.price || 0);
    }, 0);

    const baseTotal = selectedPricing.price + addOnTotal + lightingFee;
    const numberOfDates = isMultiDateBooking ? selectedDates.filter(date => date.trim() !== '').length : 1;
    
    return baseTotal * numberOfDates;
  };

  const getDepositAmount = () => {
    if (!selectedPricing) return 0;
    
    const numberOfDates = isMultiDateBooking ? selectedDates.filter(date => date.trim() !== '').length : 1;
    return selectedPricing.deposit_amount * numberOfDates;
  };

  // Check if this is a big event that requires deposit
  const isBigEvent = () => {
    if (!selectedPricing || !userProfile) return false;
    
    // Big events that require deposit:
    // 1. Full day bookings (8 hours)
    // 2. Multi-date bookings
    // 3. Promotor bookings
    return (
      selectedPricing.blocks_full_day ||
      isMultiDateBooking ||
      userProfile.role === 'promotor'
    );
  };

  const getPaymentAmount = () => {
    const total = calculateTotal();
    const deposit = getDepositAmount();
    
    if (isBigEvent()) {
      // For big events: pay total + deposit (deposit is additional security deposit)
      return total + deposit;
    }
    
    // For regular bookings, check payment option
    if (paymentOption === 'deposit') {
      return total + deposit;
    }
    return total;
  };

  const calculateLightingFee = async (selectedEndTime: string, selectedPricing: TrackPricing | null) => {
    if (!selectedEndTime || !selectedPricing) return 0;

    // Don't apply lighting fee for full day events
    if (selectedPricing.blocks_full_day) {
      return 0;
    }

    try {
      const { data: settings, error } = await supabase
        .from('system_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['lighting_fee_enabled', 'lighting_fee_threshold', 'lighting_fee_amount']);

      if (error) throw error;

      const settingsMap = settings.reduce((acc, setting) => {
        acc[setting.setting_key] = setting.setting_value;
        return acc;
      }, {} as Record<string, string>);

      const isEnabled = settingsMap.lighting_fee_enabled === 'true';
      if (!isEnabled) {
        return 0;
      }

      const threshold = settingsMap.lighting_fee_threshold; // e.g., "18:00"
      const feeAmount = parseFloat(settingsMap.lighting_fee_amount || '0');

      const endTime = new Date(`2000-01-01T${selectedEndTime}`);
      const thresholdTime = new Date(`2000-01-01T${threshold}`);

      if (endTime > thresholdTime) {
        return feeAmount;
      } else {
        return 0;
      }
    } catch (error) {
      console.error('Error calculating lighting fee:', error);
      return 0;
    }
  };

  return {
    paymentOption,
    setPaymentOption,
    calculateTotal,
    getDepositAmount,
    isBigEvent,
    getPaymentAmount,
    calculateLightingFee
  };
};